import React, { useState, useEffect } from 'react';
import { FiMail } from 'react-icons/fi';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { useNavigate } from 'react-router-dom';
import { Link } from 'react-router-dom';
import { IoMdArrowRoundBack } from 'react-icons/io';
import Login from './LoginPage';

const BQ_BASE_URL = process.env.REACT_APP_BQ_BASE_URL;

const NotCorrectOtp = () => {
  const [email, setEmail] = useState('');
  const [showOtpModal, setShowOtpModal] = useState(false);
  const [otp, setOtp] = useState(['', '', '', '', '', '']);
  const [loading, setLoading] = useState(false);
  const [otpExpired, setOtpExpired] = useState(false);
  const [countdown, setCountdown] = useState(60);
  const [canResend, setCanResend] = useState(false);
  const [showLoginModal, setShowLoginModal] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    let timer;
    if (showOtpModal && countdown > 0) {
      timer = setTimeout(() => {
        setCountdown((prev) => prev - 1);
      }, 1000);
    } else if (showOtpModal && countdown === 0) {
      setOtpExpired(true);
      setCanResend(true);
    }
    return () => clearTimeout(timer);
  }, [showOtpModal, countdown]);

  const closeLoginModal = () => {
    setShowLoginModal(false);
    navigate('/'); // Redirect to home page when modal is closed
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await fetch(`${BQ_BASE_URL}/bq/api/resend-otp`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
          accept: 'application/json',
        },
        body: `email=${encodeURIComponent(email)}`,
      });

      const data = await response.json();

      if (response.ok) {
        toast.success(data.message);
        setShowOtpModal(true);
        setOtpExpired(false);
        setCountdown(data.expires_in || 60);
        setCanResend(false);
      } else {
        toast.error(data.message || 'Something went wrong');
      }
    } catch (error) {
      toast.error('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleOtpChange = (index, value) => {
    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);

    if (value && index < 5) {
      document.getElementById(`otp-${index + 1}`).focus();
    }
  };

  const handleOtpSubmit = async (e) => {
    e.preventDefault();
    const otpCode = otp.join('');

    if (otpCode.length !== 6) {
      toast.error('Please enter a 6-digit OTP');
      return;
    }

    if (otpExpired) {
      toast.error('OTP has expired. Please request a new one.');
      return;
    }

    setLoading(true);

    try {
      const response = await fetch(`${BQ_BASE_URL}/bq/api/verify-email`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
          accept: 'application/json',
        },
        body: `email=${encodeURIComponent(email)}&otp=${otpCode}`,
      });

      const data = await response.json();

      if (response.ok && data.verified) {
        toast.success(data.message);
        // Add 3 second delay before showing login modal (you can adjust between 2000-4000ms)
        setTimeout(() => {
          setShowLoginModal(true);
        }, 3000);
      } else {
        toast.error(data.message || 'Invalid OTP');
      }
    } catch (error) {
      toast.error('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleResendOtp = async () => {
    if (!canResend) return;

    setLoading(true);
    try {
      const response = await fetch(`${BQ_BASE_URL}/bq/api/resend-otp`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
          accept: 'application/json',
        },
        body: `email=${encodeURIComponent(email)}`,
      });

      const data = await response.json();

      if (response.ok) {
        toast.success(data.message);
        setOtp(['', '', '', '', '', '']);
        setOtpExpired(false);
        setCountdown(data.expires_in || 60);
        setCanResend(false);
      } else {
        toast.error(data.message || 'Failed to resend OTP');
      }
    } catch (error) {
      toast.error('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-white flex flex-col items-center justify-center px-4">
      <ToastContainer position="top-center" autoClose={5000} />

      <Link
        to="/"
        className="absolute top-24 left-8 text-black hover:text-gray-700 transition font-semibold text-2xl"
      >
        <IoMdArrowRoundBack size={32} />
      </Link>

      {/* Main Container */}
      <div className="w-full max-w-lg">
        {/* Info Banner */}
        <div className="bg-blue-50 border border-blue-300 p-4 rounded mb-6 text-sm text-gray-700">
          Your account registration is almost complete! To finish setting up your account, please
          enter your email address below. We'll send you a new verification OTP to confirm your
          email address. Once verified, you'll be able to log in to your account
        </div>

        {/* Main Card */}
        <div className="bg-gray-100 border rounded">
          <div className="bg-white px-6 py-8 rounded-t shadow-sm border-b">
            <h2 className="text-2xl font-bold text-gray-800 mb-8 text-center">
              Verify Your Account
            </h2>

            <form className="space-y-6" onSubmit={handleSubmit}>
              {/* Email Field */}
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                  Email
                </label>
                <div className="relative">
                  <span className="absolute inset-y-0 left-0 pl-3 flex items-center">
                    <FiMail className="text-gray-400" />
                  </span>
                  <input
                    type="email"
                    id="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="Enter your email"
                    className="pl-10 pr-3 py-2 border border-gray-300 rounded w-full focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    required
                  />
                </div>
              </div>

              {/* Submit Button */}
              <button
                type="submit"
                disabled={loading}
                className={`w-full bg-black hover:text-gray-300 text-white py-2 rounded text-sm font-medium transition-colors ${
                  loading ? 'opacity-70 cursor-not-allowed' : ''
                }`}
              >
                {loading ? 'Sending...' : 'Send verify otp'}
              </button>

              {/* Back to Sign Up Link */}
              <div className="text-center mt-4">
                <button
                  type="button"
                  onClick={() => setShowLoginModal(true)}
                  className="text-blue-600 hover:underline text-sm font-medium"
                >
                  Back to Sign In
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>

      {/* OTP Modal */}
      {showOtpModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md relative">
            <button
              type="button"
              onClick={() => {
                setShowOtpModal(false);
                setOtp(['', '', '', '', '', '']);
                setOtpExpired(false);
                setCountdown(60);
              }}
              className="absolute top-4 right-4 text-gray-500 hover:text-gray-700 focus:outline-none"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-6 w-6"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>

            <div className="p-6">
              <h3 className="text-xl font-bold text-gray-800 mb-4 text-center">Enter OTP</h3>
              <p className="text-sm text-gray-600 mb-6 text-center">
                We've sent a 6-digit verification code to {email}
              </p>

              <form onSubmit={handleOtpSubmit}>
                <div className="flex justify-center space-x-2 mb-6">
                  {[0, 1, 2, 3, 4, 5].map((index) => (
                    <input
                      key={index}
                      id={`otp-${index}`}
                      type="text"
                      maxLength="1"
                      value={otp[index]}
                      onChange={(e) => handleOtpChange(index, e.target.value.replace(/\D/, ''))}
                      className="w-12 h-12 border border-gray-300 rounded text-center text-xl focus:outline-none focus:ring-2 focus:ring-black"
                      pattern="\d*"
                      required
                      disabled={otpExpired}
                    />
                  ))}
                </div>

                <div className="mb-4 text-center text-sm">
                  {countdown > 0 ? (
                    <span className="text-gray-600">OTP expires in {countdown} seconds</span>
                  ) : (
                    <span className="text-red-600">OTP has expired</span>
                  )}
                </div>

                <div className="flex space-x-3">
                  <button
                    type="submit"
                    disabled={loading || otpExpired}
                    className={`w-full bg-black hover:text-gray-300 text-white py-2 rounded font-medium transition-colors ${
                      loading || otpExpired ? 'opacity-70 cursor-not-allowed' : ''
                    }`}
                  >
                    {loading ? 'Verifying...' : 'Verify OTP'}
                  </button>
                </div>
              </form>

              <div className="mt-4 text-center flex justify-center items-center gap-1 text-sm">
                <span className="text-gray-600">Didn't receive the OTP?</span>
                <button
                  type="button"
                  onClick={handleResendOtp}
                  disabled={!canResend || loading}
                  className={`text-blue-600 hover:underline font-medium ${
                    !canResend ? 'opacity-50 cursor-not-allowed' : ''
                  }`}
                >
                  Resend OTP
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Login Modal */}
      {showLoginModal && (
        <div className="fixed inset-0 bg-white bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md relative">
            <button
              type="button"
              onClick={closeLoginModal}
              className="absolute top-4 right-4 text-gray-500 hover:text-gray-700 focus:outline-none"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-6 w-6"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
            <Login closeModal={closeLoginModal} />
          </div>
        </div>
      )}
    </div>
  );
};

export default NotCorrectOtp;

// import React, { useState, useEffect } from "react";
// import { FiMail } from "react-icons/fi";
// import { toast, ToastContainer } from "react-toastify";
// import "react-toastify/dist/ReactToastify.css";
// import { useNavigate } from "react-router-dom";
// import { Link } from "react-router-dom";
// import { IoMdArrowRoundBack } from "react-icons/io";

// const BQ_BASE_URL = process.env.REACT_APP_BQ_BASE_URL;

// const NotCorrectOtp = () => {
//   const [email, setEmail] = useState("");
//   const [showOtpModal, setShowOtpModal] = useState(false);
//   const [otp, setOtp] = useState(["", "", "", "", "", ""]);
//   const [loading, setLoading] = useState(false);
//   const [otpExpired, setOtpExpired] = useState(false);
//   const [countdown, setCountdown] = useState(60);
//   const [canResend, setCanResend] = useState(false);
//   const navigate = useNavigate();

//   useEffect(() => {
//     let timer;
//     if (showOtpModal && countdown > 0) {
//       timer = setTimeout(() => {
//         setCountdown((prev) => prev - 1);
//       }, 1000);
//     } else if (showOtpModal && countdown === 0) {
//       setOtpExpired(true);
//       setCanResend(true);
//     }
//     return () => clearTimeout(timer);
//   }, [showOtpModal, countdown]);

//   const handleSubmit = async (e) => {
//     e.preventDefault();
//     setLoading(true);

//     try {
//       const response = await fetch(`${BQ_BASE_URL}/bq/api/resend-otp`, {
//         method: "POST",
//         headers: {
//           "Content-Type": "application/x-www-form-urlencoded",
//           accept: "application/json",
//         },
//         body: `email=${encodeURIComponent(email)}`,
//       });

//       const data = await response.json();

//       if (response.ok) {
//         toast.success(data.message);
//         setShowOtpModal(true);
//         setOtpExpired(false);
//         setCountdown(data.expires_in || 60);
//         setCanResend(false);
//       } else {
//         toast.error(data.message || "Something went wrong");
//       }
//     } catch (error) {
//       toast.error("Network error. Please try again.");
//     } finally {
//       setLoading(false);
//     }
//   };

//   const handleOtpChange = (index, value) => {
//     const newOtp = [...otp];
//     newOtp[index] = value;
//     setOtp(newOtp);

//     if (value && index < 5) {
//       document.getElementById(`otp-${index + 1}`).focus();
//     }
//   };

//   const handleOtpSubmit = async (e) => {
//     e.preventDefault();
//     const otpCode = otp.join("");

//     if (otpCode.length !== 6) {
//       toast.error("Please enter a 6-digit OTP");
//       return;
//     }

//     if (otpExpired) {
//       toast.error("OTP has expired. Please request a new one.");
//       return;
//     }

//     setLoading(true);

//     try {
//       const response = await fetch(`${BQ_BASE_URL}/bq/api/verify-email`, {
//         method: "POST",
//         headers: {
//           "Content-Type": "application/x-www-form-urlencoded",
//           accept: "application/json",
//         },
//         body: `email=${encodeURIComponent(email)}&otp=${otpCode}`,
//       });

//       const data = await response.json();

//       if (response.ok && data.verified) {
//         toast.success(data.message);
//         setTimeout(() => {
//           navigate("/login");
//         }, 5000);
//       } else {
//         toast.error(data.message || "Invalid OTP");
//       }
//     } catch (error) {
//       toast.error("Network error. Please try again.");
//     } finally {
//       setLoading(false);
//     }
//   };

//   const handleResendOtp = async () => {
//     if (!canResend) return;

//     setLoading(true);
//     try {
//       const response = await fetch(`${BQ_BASE_URL}/bq/api/resend-otp`, {
//         method: "POST",
//         headers: {
//           "Content-Type": "application/x-www-form-urlencoded",
//           accept: "application/json",
//         },
//         body: `email=${encodeURIComponent(email)}`,
//       });

//       const data = await response.json();

//       if (response.ok) {
//         toast.success(data.message);
//         setOtp(["", "", "", "", "", ""]);
//         setOtpExpired(false);
//         setCountdown(data.expires_in || 60);
//         setCanResend(false);
//       } else {
//         toast.error(data.message || "Failed to resend OTP");
//       }
//     } catch (error) {
//       toast.error("Network error. Please try again.");
//     } finally {
//       setLoading(false);
//     }
//   };

//   return (
//     <div className="min-h-screen bg-white flex flex-col items-center justify-center px-4">
//       <ToastContainer position="top-center" autoClose={5000} />

//       <Link
//         to="/"
//         className="absolute top-28 left-5 text-black hover:text-gray-700 transition font-semibold text-2xl"
//       >
//         <IoMdArrowRoundBack size={32} />
//       </Link>

//       {/* Main Container */}
//       <div className="w-full max-w-lg">
//         {/* Info Banner */}
//         <div className="bg-blue-50 border border-blue-300 p-4 rounded mb-6 text-sm text-gray-700">
//           Your account registration is almost complete! To finish setting up
//           your account, please enter your email address below. We'll send you a
//           new verification OTP to confirm your email address. Once verified,
//           you'll be able to log in to your account.
//         </div>

//         {/* Main Card */}
//         <div className="bg-gray-100 border rounded">
//           <div className="bg-white px-6 py-8 rounded-t shadow-sm border-b">
//             <h2 className="text-2xl font-bold text-gray-800 mb-8 text-center">
//               Verify Your Account
//             </h2>

//             <form className="space-y-6" onSubmit={handleSubmit}>
//               {/* Email Field */}
//               <div>
//                 <label
//                   htmlFor="email"
//                   className="block text-sm font-medium text-gray-700 mb-1"
//                 >
//                   E-mail:
//                 </label>
//                 <div className="relative">
//                   <span className="absolute inset-y-0 left-0 pl-3 flex items-center">
//                     <FiMail className="text-gray-400" />
//                   </span>
//                   <input
//                     type="email"
//                     id="email"
//                     value={email}
//                     onChange={(e) => setEmail(e.target.value)}
//                     placeholder="Enter your email"
//                     className="pl-10 pr-3 py-2 border border-gray-300 rounded w-full focus:outline-none focus:ring-blue-500 focus:border-blue-500"
//                     required
//                   />
//                 </div>
//               </div>

//               {/* Submit Button */}
//               <button
//                 type="submit"
//                 disabled={loading}
//                 className={`w-full bg-black hover:text-gray-300 text-white py-2 rounded text-sm font-medium transition-colors ${
//                   loading ? "opacity-70 cursor-not-allowed" : ""
//                 }`}
//               >
//                 {loading ? "Sending..." : "Send verify otp"}
//               </button>

//               {/* Back to Sign Up Link */}
//               <div className="text-center mt-4">
//                 <a
//                   href="/login"
//                   className="text-blue-600 hover:underline text-sm font-medium"
//                 >
//                   Back to Sign In
//                 </a>
//               </div>
//             </form>
//           </div>
//         </div>
//       </div>

//       {/* OTP Modal */}
//       {showOtpModal && (
//         <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
//           <div className="bg-white rounded-lg shadow-xl w-full max-w-md">
//             <div className="p-6">
//               <h3 className="text-xl font-bold text-gray-800 mb-4 text-center">
//                 Enter OTP
//               </h3>
//               <p className="text-sm text-gray-600 mb-6 text-center">
//                 We've sent a 6-digit verification code to {email}
//               </p>

//               <form onSubmit={handleOtpSubmit}>
//                 <div className="flex justify-center space-x-2 mb-6">
//                   {[0, 1, 2, 3, 4, 5].map((index) => (
//                     <input
//                       key={index}
//                       id={`otp-${index}`}
//                       type="text"
//                       maxLength="1"
//                       value={otp[index]}
//                       onChange={(e) =>
//                         handleOtpChange(index, e.target.value.replace(/\D/, ""))
//                       }
//                       className="w-12 h-12 border border-gray-300 rounded text-center text-xl focus:outline-none focus:ring-2 focus:ring-black"
//                       pattern="\d*"
//                       required
//                       disabled={otpExpired}
//                     />
//                   ))}
//                 </div>

//                 <div className="mb-4 text-center text-sm">
//                   {countdown > 0 ? (
//                     <span className="text-gray-600">
//                       OTP expires in {countdown} seconds
//                     </span>
//                   ) : (
//                     <span className="text-red-600">OTP has expired</span>
//                   )}
//                 </div>

//                 <div className="flex space-x-3">
//                   <button
//                     type="button"
//                     onClick={() => {
//                       setShowOtpModal(false);
//                       setOtp(["", "", "", "", "", ""]);
//                       setOtpExpired(false);
//                       setCountdown(60);
//                     }}
//                     className="flex-1 bg-gray-200 hover:bg-gray-300 text-gray-800 py-2 rounded font-medium transition-colors"
//                   >
//                     Cancel
//                   </button>
//                   <button
//                     type="submit"
//                     disabled={loading || otpExpired}
//                     className={`flex-1 bg-black hover:text-gray-300 text-white py-2 rounded font-medium transition-colors ${
//                       loading || otpExpired
//                         ? "opacity-70 cursor-not-allowed"
//                         : ""
//                     }`}
//                   >
//                     {loading ? "Verifying..." : "Verify OTP"}
//                   </button>
//                 </div>
//               </form>

//               <div className="mt-4 text-center flex justify-center items-center gap-1 text-sm">
//                 <span className="text-gray-600">Didn't receive the OTP?</span>
//                 <button
//                   type="button"
//                   onClick={handleResendOtp}
//                   disabled={!canResend || loading}
//                   className={`text-blue-600 hover:underline font-medium ${
//                     !canResend ? "opacity-50 cursor-not-allowed" : ""
//                   }`}
//                 >
//                   Resend OTP
//                 </button>
//               </div>
//             </div>
//           </div>
//         </div>
//       )}
//     </div>
//   );
// };

// export default NotCorrectOtp;
